import {useMemo, useState} from 'react';
import {Flag, PiggyBank, Wallet} from 'lucide-react';
import {fullMoney, money, number} from '../lib/format';
import {useIntent, type LoanIntent} from '../shell/AppLink';
import {useStore} from '../store/StoreContext';
import {isPublic} from '../store/types';
import {
  Card,
  Field,
  FormGrid,
  Screen,
  Segmented,
  Select,
  Stat,
  TextInput,
} from '../ui/primitives';

type Method = 'giam-dan' | 'tra-deu';

interface MonthRow {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
  rate: number;
}

interface YearRow {
  year: number;
  principal: number;
  interest: number;
  balance: number;
}

interface Schedule {
  months: MonthRow[];
  years: YearRow[];
  totalInterest: number;
  first: number;
  peak: number;
  last: number;
  /** Month the borrower crosses half the principal — the psychological midpoint. */
  halfway: number;
}

/**
 * Amortisation with a promotional period, which is how Vietnamese mortgages are
 * actually sold: a low fixed rate for the first months, then a floating one.
 *
 * Modelling that is not a detail. The instalment AFTER the promotion ends is the
 * number that decides whether a household can afford the flat, and it is the one
 * a plain calculator never shows — so this one reports the peak payment beside
 * the first payment, always.
 */
function amortise(
  amount: number,
  promoRate: number,
  promoMonths: number,
  rate: number,
  years: number,
  method: Method,
): Schedule {
  const months = Math.max(1, Math.round(years * 12));
  const flatPrincipal = amount / months;

  const rows: MonthRow[] = [];
  const yearRows: YearRow[] = [];

  let balance = amount;
  let totalInterest = 0;
  let annuity = 0;
  let lastRate = -1;
  let yearPrincipal = 0;
  let yearInterest = 0;
  let paidPrincipal = 0;
  let halfway = 0;

  for (let month = 1; month <= months; month += 1) {
    const annual = month <= promoMonths ? promoRate : rate;
    const monthly = annual / 100 / 12;

    if (method === 'tra-deu' && monthly !== lastRate) {
      // The rate changed: re-solve the level payment over what is LEFT, exactly
      // as a bank re-quotes the instalment when the promotion ends.
      const remaining = months - month + 1;
      annuity =
        monthly === 0 ? balance / remaining : (balance * monthly) / (1 - Math.pow(1 + monthly, -remaining));
      lastRate = monthly;
    }

    const interest = balance * monthly;
    const principal = method === 'tra-deu' ? Math.max(0, annuity - interest) : flatPrincipal;
    const payment = principal + interest;

    balance = Math.max(0, balance - principal);
    totalInterest += interest;
    paidPrincipal += principal;
    if (!halfway && paidPrincipal >= amount / 2) halfway = month;

    rows.push({balance, interest, month, payment, principal, rate: annual});

    yearPrincipal += principal;
    yearInterest += interest;
    if (month % 12 === 0 || month === months) {
      yearRows.push({
        balance,
        interest: yearInterest,
        principal: yearPrincipal,
        year: Math.ceil(month / 12),
      });
      yearPrincipal = 0;
      yearInterest = 0;
    }
  }

  return {
    first: rows[0]?.payment ?? 0,
    halfway: halfway || months,
    last: rows[rows.length - 1]?.payment ?? 0,
    months: rows,
    peak: rows.reduce((best, row) => Math.max(best, row.payment), 0),
    totalInterest,
    years: yearRows,
  };
}

/** The instalment a bank will lend against, inverted back into a loan size. */
function loanFromPayment(payment: number, annualRate: number, years: number): number {
  const months = Math.max(1, Math.round(years * 12));
  const monthly = annualRate / 100 / 12;
  if (monthly === 0) return payment * months;
  return (payment * (1 - Math.pow(1 + monthly, -months))) / monthly;
}

export function LoanApp() {
  const {listings} = useStore();
  const [label, setLabel] = useState('');
  const [price, setPrice] = useState(4_250_000_000);
  const [ratio, setRatio] = useState(70);
  const [promoRate, setPromoRate] = useState(7.5);
  const [promoMonths, setPromoMonths] = useState(12);
  const [rate, setRate] = useState(11);
  const [years, setYears] = useState(20);
  const [method, setMethod] = useState<Method>('giam-dan');
  const [income, setIncome] = useState(45_000_000);
  const [savedUp, setSavedUp] = useState(600_000_000);
  const [monthlySaving, setMonthlySaving] = useState(15_000_000);
  const [view, setView] = useState<'nam' | 'thang'>('nam');

  useIntent<LoanIntent>('loan', (intent) => {
    if (typeof intent.price === 'number') setPrice(intent.price);
    if (intent.label) setLabel(intent.label);
  });

  const amount = Math.round((price * ratio) / 100);
  const equity = price - amount;

  const schedule = useMemo(
    () => amortise(amount, promoRate, promoMonths, rate, years, method),
    [amount, method, promoMonths, promoRate, rate, years],
  );

  // Banks in Vietnam size a loan so the instalment stays under ~40% of income.
  const affordablePayment = income * 0.4;
  const affordableLoan = loanFromPayment(affordablePayment, rate, years);
  const affordablePrice = ratio > 0 ? (affordableLoan * 100) / ratio : 0;
  const monthsToEquity =
    equity <= savedUp ? 0 : monthlySaving > 0 ? Math.ceil((equity - savedUp) / monthlySaving) : Infinity;

  const maxYearTotal = Math.max(
    ...schedule.years.map((row) => row.principal + row.interest),
    1,
  );

  const milestones = [
    {
      note: `${money(schedule.first)} mỗi tháng trong ${promoMonths} tháng ưu đãi`,
      title: 'Tháng đầu tiên',
      when: 'Tháng 1',
    },
    ...(promoMonths > 0 && promoMonths < years * 12
      ? [
          {
            note: `Khoản trả tăng lên ${money(schedule.months[promoMonths]?.payment ?? 0)} khi lãi suất thả nổi ${rate}%`,
            title: 'Hết ưu đãi lãi suất',
            when: `Tháng ${promoMonths + 1}`,
          },
        ]
      : []),
    {
      note: `Đã trả được một nửa số nợ gốc (${money(amount / 2)})`,
      title: 'Trả xong 50% gốc',
      when: `Tháng ${schedule.halfway}`,
    },
    {
      note: `Tổng lãi đã trả ${money(schedule.totalInterest)}`,
      title: 'Tất toán khoản vay',
      when: `Tháng ${years * 12}`,
    },
  ];

  return (
    <Screen>
      <Card title={label ? `Khoản vay cho: ${label}` : 'Khoản vay'}>
        <FormGrid>
          <Field label="Lấy giá từ một tin đăng" wide>
            <Select
              value=""
              onChange={(id) => {
                const listing = listings.find((entry) => entry.id === id);
                if (!listing) return;
                setPrice(listing.price);
                setLabel(listing.title);
              }}
              options={[
                {label: label ? `Đang tính: ${label}` : 'Nhập giá thủ công', value: ''},
                ...listings
                  .filter((listing) => isPublic(listing) && listing.purpose === 'ban')
                  .map((listing) => ({
                    label: `${listing.code} · ${money(listing.price)} · ${listing.title}`,
                    value: listing.id,
                  })),
              ]}
            />
          </Field>
          <Field label="Giá trị bất động sản (VND)" hint={fullMoney(price)}>
            <TextInput type="number" value={price} onChange={(value) => setPrice(Number(value) || 0)} />
          </Field>
          <Field label="Tỉ lệ vay (%)" hint={`Vay ${money(amount)} · tự có ${money(equity)}`}>
            <TextInput
              type="number"
              value={ratio}
              onChange={(value) => setRatio(Math.min(100, Math.max(0, Number(value) || 0)))}
            />
          </Field>
          <Field label="Lãi suất ưu đãi (%/năm)">
            <TextInput
              type="number"
              value={promoRate}
              onChange={(value) => setPromoRate(Math.max(0, Number(value) || 0))}
            />
          </Field>
          <Field label="Số tháng ưu đãi">
            <TextInput
              type="number"
              value={promoMonths}
              onChange={(value) => setPromoMonths(Math.max(0, Math.min(years * 12, Number(value) || 0)))}
            />
          </Field>
          <Field label="Lãi suất sau ưu đãi (%/năm)">
            <TextInput
              type="number"
              value={rate}
              onChange={(value) => setRate(Math.max(0, Number(value) || 0))}
            />
          </Field>
          <Field label="Thời hạn vay (năm)">
            <TextInput
              type="number"
              value={years}
              onChange={(value) => setYears(Math.min(35, Math.max(1, Number(value) || 1)))}
            />
          </Field>
          <Field label="Phương thức trả nợ" wide>
            <Select
              value={method}
              onChange={(value) => setMethod(value as Method)}
              options={[
                {label: 'Dư nợ giảm dần (trả nhiều lúc đầu)', value: 'giam-dan'},
                {label: 'Trả đều hàng tháng (annuity)', value: 'tra-deu'},
              ]}
            />
          </Field>
        </FormGrid>
      </Card>

      <div className="mt-4 grid grid-cols-2 gap-3 @3xl:grid-cols-4">
        <Stat
          label="Trả tháng đầu"
          value={money(schedule.first)}
          hint={`Ưu đãi ${promoRate}%/năm`}
        />
        <Stat
          label="Tháng cao nhất"
          value={money(schedule.peak)}
          hint={promoMonths > 0 ? 'Sau khi hết ưu đãi' : 'Trong suốt kỳ vay'}
        />
        <Stat label="Tổng lãi phải trả" value={money(schedule.totalInterest)} />
        <Stat
          label="Tổng phải trả"
          value={money(amount + schedule.totalInterest)}
          hint={`Gốc ${money(amount)}`}
        />
      </div>

      <div className="h-4" />

      <Card
        title="Kế hoạch trả nợ"
        action={
          <Segmented
            value={view}
            onChange={setView}
            options={[
              {label: 'Theo năm', value: 'nam'},
              {label: '12 tháng đầu', value: 'thang'},
            ]}
          />
        }
      >
        {view === 'nam' ? (
          <ul className="space-y-2">
            {schedule.years.map((row) => {
              const total = row.principal + row.interest;
              const width = (total / maxYearTotal) * 100;
              const principalShare = total > 0 ? (row.principal / total) * 100 : 0;
              return (
                <li key={row.year} className="flex items-center gap-3">
                  <span className="w-14 shrink-0 text-[11.5px] font-semibold text-ink-3">
                    Năm {row.year}
                  </span>
                  <span className="sunken-sm h-6 min-w-0 flex-1 overflow-hidden rounded-full">
                    <span className="flex h-full" style={{width: `${width}%`}}>
                      <span
                        className="h-full bg-accent"
                        style={{width: `${principalShare}%`}}
                        title="Gốc"
                      />
                      <span
                        className="h-full bg-[var(--color-ink-3)]"
                        style={{width: `${100 - principalShare}%`}}
                        title="Lãi"
                      />
                    </span>
                  </span>
                  <span className="w-24 shrink-0 text-right text-[12px] font-semibold tabular-nums text-ink">
                    {money(total)}
                  </span>
                  <span className="hidden w-28 shrink-0 text-right text-[11.5px] tabular-nums text-ink-3 @2xl:block">
                    còn {money(row.balance)}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[440px] border-collapse text-[13px]">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-[0.12em] text-ink-3">
                  <th className="px-3 py-2 font-bold">Tháng</th>
                  <th className="px-3 py-2 text-right font-bold">Phải trả</th>
                  <th className="px-3 py-2 text-right font-bold">Gốc</th>
                  <th className="px-3 py-2 text-right font-bold">Lãi</th>
                  <th className="px-3 py-2 text-right font-bold">Dư nợ</th>
                </tr>
              </thead>
              <tbody>
                {schedule.months.slice(0, 12).map((row) => (
                  <tr key={row.month} className="border-t border-line-soft">
                    <td className="px-3 py-2 text-ink-2">
                      Tháng {row.month}
                      <span className="ml-1.5 text-[10.5px] text-ink-3">{row.rate}%</span>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-ink">
                      {money(row.payment)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-2">
                      {money(row.principal)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-2">
                      {money(row.interest)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-2">
                      {money(row.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-3 flex flex-wrap items-center gap-4 text-[11.5px] text-ink-3">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-accent" /> Gốc
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[var(--color-ink-3)]" /> Lãi
          </span>
        </p>
      </Card>

      <div className="h-4" />

      <Card title="Các mốc cần nhớ">
        <ol className="space-y-3">
          {milestones.map((milestone) => (
            <li key={milestone.title} className="flex gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
                <Flag size={15} />
              </span>
              <div className="min-w-0">
                <p className="text-[13px] font-bold text-ink">
                  {milestone.title}
                  <span className="ml-2 text-[11.5px] font-semibold text-ink-3">
                    {milestone.when}
                  </span>
                </p>
                <p className="text-[12.5px] text-ink-2">{milestone.note}</p>
              </div>
            </li>
          ))}
        </ol>
      </Card>

      <div className="h-4" />

      <div className="grid gap-4 @3xl:grid-cols-2">
        <Card title="Khả năng chi trả">
          <Field label="Thu nhập của gia đình mỗi tháng (VND)" hint={fullMoney(income)}>
            <TextInput
              type="number"
              value={income}
              onChange={(value) => setIncome(Math.max(0, Number(value) || 0))}
            />
          </Field>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Stat
              label="Trả góp tối đa"
              value={money(affordablePayment)}
              hint="40% thu nhập — ngưỡng ngân hàng thường chấp nhận"
            />
            <Stat
              label="Giá nhà phù hợp"
              value={money(affordablePrice)}
              hint={`Vay tối đa ${money(affordableLoan)} trong ${years} năm`}
            />
          </div>
          <p className="mt-3 flex items-start gap-2 text-[12.5px] text-ink-2">
            <Wallet size={15} className="mt-0.5 shrink-0 text-ink-3" />
            {schedule.peak <= affordablePayment
              ? 'Khoản vay này nằm trong khả năng chi trả, kể cả tháng cao nhất sau ưu đãi.'
              : `Tháng cao nhất (${money(schedule.peak)}) vượt ngưỡng an toàn. Cân nhắc kéo dài kỳ hạn, giảm tỉ lệ vay hoặc chọn căn nhỏ hơn.`}
          </p>
        </Card>

        <Card title="Kế hoạch tích luỹ vốn tự có">
          <FormGrid>
            <Field label="Đang có (VND)" hint={fullMoney(savedUp)}>
              <TextInput
                type="number"
                value={savedUp}
                onChange={(value) => setSavedUp(Math.max(0, Number(value) || 0))}
              />
            </Field>
            <Field label="Tiết kiệm mỗi tháng (VND)" hint={fullMoney(monthlySaving)}>
              <TextInput
                type="number"
                value={monthlySaving}
                onChange={(value) => setMonthlySaving(Math.max(0, Number(value) || 0))}
              />
            </Field>
          </FormGrid>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Stat label="Vốn tự có cần có" value={money(equity)} hint={`${100 - ratio}% giá trị`} />
            <Stat
              label="Thời gian còn thiếu"
              value={
                monthsToEquity === 0
                  ? 'Đủ rồi'
                  : Number.isFinite(monthsToEquity)
                    ? `${number(monthsToEquity)} tháng`
                    : '—'
              }
              hint={
                monthsToEquity === 0
                  ? 'Có thể xuống tiền ngay'
                  : Number.isFinite(monthsToEquity)
                    ? `≈ ${(monthsToEquity / 12).toFixed(1).replace('.', ',')} năm nữa`
                    : 'Nhập số tiền tiết kiệm mỗi tháng'
              }
            />
          </div>
          <p className="mt-3 flex items-start gap-2 text-[12.5px] text-ink-2">
            <PiggyBank size={15} className="mt-0.5 shrink-0 text-ink-3" />
            Ngoài vốn tự có, hãy để dành thêm khoảng 2–3% giá trị cho thuế trước bạ, phí công chứng
            và phí sang tên.
          </p>
        </Card>
      </div>
    </Screen>
  );
}
